import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { strings } from '/imports/strings';
import { ModelClass, Docmnt, ListMethodArg, ListMethodResponse } from '/imports/types';
import { Button } from '/imports/ui/Button';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { Icon } from '/imports/ui/Icon';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { useUser } from '/imports/hooks';
import { createActiveFilter } from '/imports/ui/ActiveFilter';
import { ListMethodCatalog, ListMethodCatalogProps } from '/imports/ui/ListMethodCatalog';
import { Column } from '/imports/ui/DataTable';
import { MethodDeclaration } from '/imports/methods';

export const ModelListPageStyled = styled(Page)`
  img {
    max-height: 3.5rem;
  }
  .page-body {
    height: 0;
  }
`;

interface ModelListPageProps<M extends Docmnt> extends Omit<ListMethodCatalogProps<M>, 'listMethod' | 'allIds'> {
  modelClass: ModelClass<M>;
  columns: Column<M>[];
  permission?: (user?: Meteor.User) => boolean | undefined;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  defaultSort?: { [k in keyof M]?: 1 | -1 };
  query?: Mongo.Query<M> | (() => Mongo.Query<M>);
  tools?: React.ReactNode;
  extraFilters?: React.ReactNode | (() => React.ReactNode);
  styled?: any;
  className?: string;
  listMethod?: MethodDeclaration<ListMethodArg<M>, ListMethodResponse<M>>;
}

/** Generic list page for a model class: heading, filters, and a paginated catalog. */
export const ModelListPage = <M extends Docmnt>(props: ModelListPageProps<M>) => {
  const { permission, modelClass, columns, header, footer, defaultSort, extraFilters, className, styled, tools, data } = props;
  const { pathPrefix, pluralName, icon, updateMethod, deleteMethod } = modelClass;
  const listMethod = props.listMethod || modelClass.listMethod;
  if (!listMethod || !updateMethod || !pathPrefix || !pluralName) {
    throw `modelClass is misconfigured for ModelFormPage: ${modelClass.collection}`;
  }
  const user = useUser();
  const propsQuery = typeof props.query === 'function' ? props.query() : props.query;
  const [query, setQuery] = useState<Mongo.Query<M>>({ active: true, ...propsQuery } as any);
  useEffect(() => { setQuery({ ...query, ...propsQuery }); }, [JSON.stringify(propsQuery || {})]);
  const ActiveFilter = useMemo(() => createActiveFilter<M>(query, setQuery), []);
  if ((permission && !permission?.(user)) && user && !Meteor.loggingIn()) {
    return <NotPermittedPage />;
  }
  return React.createElement(styled || ModelListPageStyled, {
    className: `page model-list-page ${className || ''}`,
    breadcrumbs: [{ path: pathPrefix, label: pluralName }]
  },
  <>
    {
      header ||
        <PageHeading
          left={<h1>{icon && <Icon icon={icon} /> || null}{pluralName}</h1>}
          right={<div><Link to={`${pathPrefix}/new`}><Button className="btn primary new-button">{strings.New()}</Button></Link></div>}
        />
    }
    <PageBody>
      <ListMethodCatalog
        {...props}
        data={data || []}
        columns={columns as Column<Docmnt>[]}
        query={query}
        defaultSort={defaultSort}
        listMethod={listMethod}
        updateMethod={updateMethod as MethodDeclaration<Partial<Docmnt>, string>}
        deleteMethod={deleteMethod}
        tools={
          tools ||
            <div className="filters">
              <span className="filters-label">{strings.Filters()}:</span>
              {typeof extraFilters === 'function' ? extraFilters() : extraFilters}
              <ActiveFilter.Component />
            </div>
        }
      />
      { footer || null }
    </PageBody>
  </>
  );
};